import React, { useEffect, useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";
import { ArrowRight, Envelope, Lock, Eye, EyeSlash } from "@phosphor-icons/react";
import ForgotPasswordDialog from "@/pages/auth/ForgotPasswordDialog";

const LOGO_SB = "/logo-sb.png";
// Pre-processed white-on-transparent variant of logo-surbakti.jpg. Generated
// once via a Python script (see finish notes) so the browser doesn't have to
// re-derive alpha via SVG filters at every render — cheaper + cleaner edges.
const LOGO_SB_WHITE = "/logo-surbakti-white.png";
const REMEMBER_KEY = "epool.remembered_email";

export default function LoginPage() {
  const { user, login } = useAuth();
  const [email, setEmail] = useState(() => localStorage.getItem(REMEMBER_KEY) || "");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [remember, setRemember] = useState(() => !!localStorage.getItem(REMEMBER_KEY));
  const [submitting, setSubmitting] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  // Two-step login: after the first submit, if backend returns `mfa_required`
  // we flip to the OTP prompt without leaving the page. `mfaStep` also
  // hides the password/email fields so users don't have to retype.
  const [mfaStep, setMfaStep] = useState(false);
  const [otp, setOtp] = useState("");
  const navigate = useNavigate();

  // Both admin AND admin_kantor land on Dashboard; only petugas → /loading.
  // Keep this in sync with `RootRedirect` in App.js.
  const landingFor = (role) => (role === "petugas" ? "/loading" : "/");

  useEffect(() => {
    // If user unchecks remember, clear any stored email immediately so the
    // next visit starts blank.
    if (!remember) localStorage.removeItem(REMEMBER_KEY);
  }, [remember]);

  if (user) return <Navigate to={landingFor(user.role)} replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const cleanEmail = email.trim();
      const u = await login(cleanEmail, password, mfaStep ? otp : null);
      // Remember-me persists ONLY the email (never the password / token).
      // 4-hour session cap is a hard security invariant; this checkbox is a
      // UX convenience to pre-fill the email field on the next visit.
      if (remember) localStorage.setItem(REMEMBER_KEY, cleanEmail);
      else localStorage.removeItem(REMEMBER_KEY);
      toast.success(`Selamat datang, ${u.name}`);
      navigate(landingFor(u.role));
    } catch (err) {
      // Backend flagged this account as MFA-enrolled; show the OTP prompt
      // instead of surfacing an error toast.
      if (err?.mfaRequired) {
        setMfaStep(true);
        setSubmitting(false);
        return;
      }
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const onForgotPassword = () => {
    setForgotOpen(true);
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2">
      {/* Left panel: branded splash with blended SB logo */}
      <div
        className="hidden md:flex relative overflow-hidden"
        style={{
          background:
            "radial-gradient(circle at 30% 20%, #fb923c 0%, #ea580c 45%, #9a3412 100%)",
        }}
        data-testid="login-hero"
      >
        {/* Subtle grain / noise for warmth */}
        <div
          aria-hidden
          className="absolute inset-0 opacity-[0.15] mix-blend-overlay pointer-events-none"
          style={{
            backgroundImage:
              "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='120' height='120'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%' height='100%' filter='url(%23n)' opacity='0.6'/></svg>\")",
          }}
        />

        {/* Giant SB logo watermark — pre-processed white-on-transparent
            variant of the real Surbakti logo, low opacity so it reads as a
            soft imprint on the orange gradient. */}
        <img
          src={LOGO_SB_WHITE}
          alt=""
          aria-hidden
          className="absolute left-1/2 -translate-x-1/2 bottom-[5%] w-[65%] max-w-none pointer-events-none select-none opacity-25"
          data-testid="login-hero-logo-bg"
        />

        {/* Foreground copy */}
        <div className="relative z-10 p-12 flex flex-col justify-between w-full text-white">
          <div className="flex items-center gap-3">
            {/* Real Surbakti logo — white-on-transparent PNG so the double
                circle border + inner mark render exactly like the printed
                brand asset, no extra styling needed. */}
            <div className="w-14 h-14 shrink-0 flex items-center justify-center">
              <img
                src={LOGO_SB_WHITE}
                alt="Logo Sari Bakti Jaya"
                className="w-full h-full object-contain"
              />
            </div>
            <div>
              <div className="font-black text-3xl leading-none tracking-tight">epool</div>
              <div className="text-[10px] font-mono font-bold uppercase tracking-[0.25em] text-white/90 mt-2">
                PT Sari Bakti Jaya
              </div>
            </div>
          </div>

          {/* Middle: big tagline + description */}
          <div className="max-w-md">
            <h1 className="text-4xl lg:text-5xl font-black tracking-tight leading-[1.05]">
              Sistem
              <br />
              Pengambilan
              <br />
              Semen
            </h1>
            <div className="w-16 h-0.5 bg-white/80 my-6" />
            <p className="text-white/90 text-sm leading-relaxed">
              Solusi digital untuk pengelolaan pengambilan semen yang
              lebih cepat, akurat, dan terintegrasi.
            </p>
          </div>

          <div className="text-[10px] font-mono font-bold uppercase tracking-[0.25em] text-white/80 drop-shadow-[0_1px_4px_rgba(0,0,0,0.3)]">
            Distributor Semen Indonesia Group
          </div>
        </div>
      </div>

      {/* Right panel: form — absolute-positioned copyright so the form
          gets vertically centered on the FULL viewport height (not the
          space-above-copyright). */}
      <div className="relative flex items-center justify-center p-6 md:p-10 min-h-screen">
        <div className="w-full max-w-md">
            <div className="md:hidden mb-8 flex items-center gap-3">
              <div className="w-12 h-12 flex items-center justify-center overflow-hidden">
                <img src={LOGO_SB} alt="Logo Sari Bakti Jaya" className="w-full h-full object-contain" />
              </div>
              <div>
                <div className="font-black text-lg leading-none tracking-tight">epool</div>
                <div className="overline mt-1">PT Sari Bakti Jaya</div>
              </div>
            </div>

            <div className="overline mb-2 text-primary">Masuk ke Sistem</div>
            <h2 className="text-4xl font-black tracking-tight mb-10">
              {mfaStep ? "Verifikasi MFA" : "Login"}
            </h2>

            <form onSubmit={onSubmit} className="space-y-6" data-testid="login-form">
              {/* Email + password only shown on the first step. Once backend
                  flags `mfa_required`, we hide them so users don't retype. */}
              {!mfaStep && (
              <div>
                <label htmlFor="email" className="text-xs font-bold uppercase tracking-widest text-muted-foreground block mb-2">
                  Email
                </label>
                <div className="relative">
                  <Envelope
                    size={18}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none"
                    aria-hidden
                  />
                  <Input
                    id="email"
                    type="text"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoFocus
                    placeholder="admin@spb.local"
                    className="h-12 pl-10 rounded-lg border-border focus-visible:ring-primary/40"
                    data-testid="login-email-input"
                  />
                </div>
              </div>
              )}

              {!mfaStep && (
              <div>
                <label htmlFor="password" className="text-xs font-bold uppercase tracking-widest text-muted-foreground block mb-2">
                  Password
                </label>
                <div className="relative">
                  <Lock
                    size={18}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none"
                    aria-hidden
                  />
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    placeholder="Masukkan password"
                    className="h-12 pl-10 pr-10 rounded-lg border-border focus-visible:ring-primary/40"
                    data-testid="login-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    aria-label={showPassword ? "Sembunyikan password" : "Tampilkan password"}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    data-testid="login-password-toggle"
                  >
                    {showPassword
                      ? <EyeSlash size={18} weight="regular" />
                      : <Eye size={18} weight="regular" />}
                  </button>
                </div>
              </div>
              )}

              {mfaStep && (
                <div>
                  <label htmlFor="otp" className="text-xs font-bold uppercase tracking-widest text-muted-foreground block mb-2">
                    Kode OTP (6 digit)
                  </label>
                  <Input
                    id="otp"
                    inputMode="numeric"
                    pattern="\d{6}"
                    maxLength={6}
                    autoComplete="one-time-code"
                    autoFocus
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                    required
                    placeholder="000000"
                    className="h-14 rounded-lg border-border focus-visible:ring-primary/40 font-mono text-center text-2xl tracking-widest"
                    data-testid="login-otp-input"
                  />
                  <p className="text-xs text-muted-foreground mt-2">
                    Buka authenticator app di HP Anda dan masukkan 6-digit yang muncul.
                  </p>
                </div>
              )}

              {!mfaStep && (
              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 cursor-pointer select-none" htmlFor="remember">
                  <Checkbox
                    id="remember"
                    checked={remember}
                    onCheckedChange={(v) => setRemember(!!v)}
                    className="data-[state=checked]:bg-primary data-[state=checked]:border-primary rounded"
                    data-testid="login-remember-checkbox"
                  />
                  <span className="text-sm font-medium">Ingat saya</span>
                </label>
                <button
                  type="button"
                  onClick={onForgotPassword}
                  className="text-sm font-semibold text-primary hover:underline"
                  data-testid="login-forgot-password"
                >
                  Lupa password?
                </button>
              </div>
              )}

              <Button
                type="submit"
                disabled={submitting || (mfaStep && otp.length !== 6)}
                className="w-full rounded-lg h-12 font-bold uppercase tracking-widest text-base"
                data-testid="login-submit-button"
              >
                {submitting ? "Memproses..." : (mfaStep ? "Verifikasi" : "Masuk")}
                <ArrowRight className="ml-2" size={16} weight="bold" />
              </Button>

              {mfaStep && (
                <button
                  type="button"
                  onClick={() => { setMfaStep(false); setOtp(""); }}
                  className="w-full text-xs font-semibold text-muted-foreground hover:text-foreground"
                  data-testid="login-cancel-mfa"
                >
                  ← Kembali ke login
                </button>
              )}
            </form>
          </div>
        <div className="absolute bottom-6 left-0 right-0 text-center text-xs text-muted-foreground px-6">
          © {new Date().getFullYear()} PT SARI BAKTI JAYA. All rights reserved.
        </div>
      </div>

      <ForgotPasswordDialog
        open={forgotOpen}
        onOpenChange={setForgotOpen}
        prefillEmail={email.trim()}
      />
    </div>
  );
}
